import { functionWithFG } from '@atlaskit/platform-feature-flags-react';
import type { Extension } from '@atlassian/forge-ui/ui';
import { useEffect, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import {
	getCachedTypedChildrenContent,
	queryTypedChildrenContent,
} from '../../utils/queryTypedChildrenContent';

type Props = {
	contentContainerId: string;
	contentKey: string;
	extension: Extension;
};

const getCustomContentType = (extension: Extension) => {
	const appId = extension?.appId,
		envId = extension?.environmentId,
		customContentKey = extension?.properties?.staticView?.customContentTypeKey;

	if (!appId || !envId || !customContentKey) {
		return null;
	}

	return `forge:${appId}:${envId}:${customContentKey}`;
};

const useMacroStaticContentViewImpl = <T>({
	extension,
	contentContainerId,
	contentKey,
}: Props): { content: T | null; isLoading: boolean } => {
	const childContentType = getCustomContentType(extension);
	const hasStaticViewDefinition = !!extension?.properties?.staticView && !!childContentType;
	const cachedContentValue = hasStaticViewDefinition
		? getCachedTypedChildrenContent({ contentContainerId, childContentType })?.[contentKey]
		: null;
	const relayEnvironment = useRelayEnvironment();

	const [{ isLoading, content }, setState] = useState<{
		content: unknown | null;
		isLoading: boolean;
	}>({
		isLoading: hasStaticViewDefinition && !cachedContentValue,
		content: cachedContentValue,
	});

	useEffect(() => {
		// First render matches content.
		if (!!cachedContentValue && cachedContentValue === content) {
			return;
		}

		// No content -> content found (e.g. contentKey changed).
		if (!!cachedContentValue && !content) {
			setState({
				isLoading: false,
				content: cachedContentValue,
			});

			return;
		}

		let cancelled = false;

		// Content type not yet queried.
		(async () => {
			if (!childContentType) {
				return;
			}

			const childrenContent = await queryTypedChildrenContent({
				contentContainerId,
				childContentType,
				relayEnvironment,
			}).catch(() => null);

			if (cancelled || !childrenContent) {
				return;
			}

			setState({
				isLoading: false,
				content: childrenContent[contentKey],
			});
		})();

		return () => {
			cancelled = true;
		};
	}, [
		content,
		cachedContentValue,
		contentKey,
		childContentType,
		contentContainerId,
		relayEnvironment,
	]);

	return {
		isLoading,
		content: content as T | null,
	};
};

const defaultImpl = <T>(_: Props) => ({
	isLoading: false,
	content: null as T | null,
});

export const useMacroStaticContentView: <T>(_: Props) => {
    content: T | null;
    isLoading: boolean;
} = functionWithFG(
	'forge-ui-static-macros',
	useMacroStaticContentViewImpl,
	defaultImpl,
);
